import * as React from 'react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import moment from 'moment'

import './index.scss'
import { useFormatDates } from '../../helpers/toFormatFullDateToISO'
import StyledInput from '../../../civic-champs-shared/core/StyledInput'
import EventIcon from '@material-ui/icons/Event'
import { makeStyles } from '@material-ui/core/styles'
import cn from 'classnames'

import { DEFAULT_CALENDAR_HEIGHT, getCalendarPositionStyles } from './calendarPosition'

export interface DatePickerMultiSelectProps {
  onChange: (date: string) => void
  minDate?: string
  isDisabled?: boolean
  dates: Date[]
  datesOnScreen: number
  label?: string
  placeholder?: string
  error?: string
  belowField?: boolean
}

const useStyles = makeStyles({
  adornment: {
    // to be aligned with regular mui datepicker icon placement
    right: '25px',
  },
})

const DatePickerMultiSelect = (props: DatePickerMultiSelectProps) => {
  const { label, placeholder, dates, onChange, minDate, isDisabled, datesOnScreen, error, belowField } = props
  const classes = useStyles()

  const { timeZone } = useFormatDates()

  const [showDatepicker, setShowDatepicker] = useState(false)
  const [calendarStyles, setCalendarStyles] = useState<React.CSSProperties>({})

  const inputRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)

  const updateCalendarPosition = useCallback(() => {
    if (belowField || !showDatepicker || !inputRef.current) {
      setCalendarStyles({})
      return
    }

    const calendarHeight = calendarRef.current?.offsetHeight || DEFAULT_CALENDAR_HEIGHT
    setCalendarStyles(getCalendarPositionStyles(inputRef.current, calendarHeight))
  }, [belowField, showDatepicker])

  useLayoutEffect(() => {
    updateCalendarPosition()
  }, [updateCalendarPosition, showDatepicker])

  useEffect(() => {
    if (!showDatepicker || belowField) {
      return
    }

    window.addEventListener('resize', updateCalendarPosition)
    window.addEventListener('scroll', updateCalendarPosition, true)

    return () => {
      window.removeEventListener('resize', updateCalendarPosition)
      window.removeEventListener('scroll', updateCalendarPosition, true)
    }
  }, [belowField, showDatepicker, updateCalendarPosition])

  useEffect(() => {
    if (!showDatepicker) {
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      if (inputRef.current?.contains(target) || calendarRef.current?.contains(target)) {
        return
      }

      setShowDatepicker(false)
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showDatepicker])

  const handleClick = () => {
    setShowDatepicker(true)
  }

  const value =
    dates
      .map(date => moment(date).tz(timeZone, true).format('MM/DD/YYYY'))
      .slice(0, datesOnScreen)
      .join(', ') + (dates.length > datesOnScreen ? '...' : '')

  const handleChange = (date: Date) => {
    const formattedDate = date && moment(date).format('MM/DD/YYYY')
    onChange(formattedDate)
  }

  const openToDate = useMemo(() => (dates.length > 0 ? dates[0] : new Date()), [dates])

  const calendar = (
    <div
      ref={calendarRef}
      className={cn('custom-datepicker-multi-select-new__datepicker', {
        under: belowField,
        'custom-datepicker-multi-select-new__datepicker--portal': !belowField,
      })}
      style={!belowField ? calendarStyles : undefined}
    >
      <DatePicker
        inline
        disabled={isDisabled}
        dateFormat="MM/dd/yyyy"
        minDate={minDate ? new Date(minDate) : undefined}
        isClearable
        showYearDropdown
        onChange={handleChange}
        highlightDates={dates}
        openToDate={openToDate}
      />
    </div>
  )

  return (
    <div className="custom-datepicker-multi-select-new">
      <StyledInput
        label={label}
        placeholder={placeholder}
        ref={inputRef}
        onChange={() => {}}
        onFocus={handleClick}
        value={value}
        endAdornment={<EventIcon />}
        endAdornmentClassName={classes.adornment}
        error={error}
      />
      {showDatepicker &&
        (belowField
          ? calendar
          : createPortal(
              <div className="custom-datepicker-multi-select-new custom-datepicker-multi-select-new--portal">
                {calendar}
              </div>,
              document.body,
            ))}
    </div>
  )
}

export default DatePickerMultiSelect
